import { forwardRef, type ButtonHTMLAttributes, type MouseEvent } from 'react';
import { useActionLock } from '@breaker/action-lock';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  /**
   * In-flight-locked press handler (Unit D). The button disables and spins
   * from the tap until the promise settles; re-entrant clicks are dropped.
   * Errors never throw out of the handler — surface them inside `action`
   * (e.g. mutateAsync + toast in a catch). Mutually exclusive with onClick
   * for mutations; onClick stays for open-dialog/navigation buttons.
   */
  action?: () => Promise<unknown>;
}

const variants: Record<Variant, string> = {
  primary:
    'bg-breaker-identity text-white hover:bg-breaker-identity-hover active:bg-breaker-identity-hover disabled:bg-breaker-mute rounded-full',
  secondary:
    'bg-white text-breaker-ink border border-breaker-line-2 hover:bg-breaker-ground-2 active:bg-breaker-line disabled:text-breaker-mute',
  ghost:
    'bg-transparent text-breaker-ink hover:bg-breaker-ground-2 active:bg-breaker-line disabled:text-breaker-mute',
  danger:
    'bg-breaker-red text-white hover:bg-breaker-red-deep active:bg-breaker-red-deep disabled:bg-breaker-mute',
};

const sizes: Record<Size, string> = {
  // Padding checked against the pill primary variant (rounded-full) at each
  // height — 12/16/20px horizontal clears the corner radius comfortably, so
  // this stays shared across variants rather than forking per-shape.
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-5 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      loading,
      disabled,
      children,
      action,
      onClick,
      ...rest
    },
    ref,
  ) => {
    const lock = useActionLock();
    const busy = loading || lock.locked;
    const handleClick = action
      ? (e: MouseEvent<HTMLButtonElement>) => {
          onClick?.(e);
          lock.run(action);
        }
      : onClick;
    return (
      <button
        ref={ref}
        onClick={handleClick}
        disabled={disabled || busy}
        className={cn(
          'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-all duration-200 ease-out active:scale-[0.98]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breaker-identity focus-visible:ring-offset-2 focus-visible:ring-offset-breaker-ground',
          'disabled:cursor-not-allowed disabled:active:scale-100',
          variants[variant],
          sizes[size],
          className,
        )}
        {...rest}
      >
        {busy ? (
          <span
            className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
            aria-hidden
          />
        ) : null}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';
